import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CreditCard, Icon } from '../ui/Icon';
import Section from '../ui/Section';
import Spinner from '../ui/Spinner';
import PaywallModal from './PaywallModal';
import { billingApi } from '../../api/billing.api';
import { useAuthStore } from '../../stores/auth.store';
import { useDateLocale } from '../../utils/dateLocale';
import { toast } from '../ui/Toast';

const STORE_NAME = { APP_STORE: 'App Store', PLAY_STORE: 'Google Play' } as const;

/**
 * ADR-0023: the account's plan. A web subscription is managed in Stripe's
 * portal; one bought in an app can only be managed in that store, so that is
 * all this says about it.
 */
export default function PlanSection() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const user = useAuthStore(s => s.user);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [opening, setOpening] = useState(false);

  async function openPortal() {
    setOpening(true);
    try {
      const { url } = await billingApi.openPortal();
      window.location.href = url;
    } catch {
      toast(t('billing.paywall.error'), 'error');
      setOpening(false);
    }
  }

  if (!user || user.isAdmin) return null;

  return (
    <Section title={t('account.plan.title')}>
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          {user.isPro && user.proUntil
            ? t('account.plan.proUntil', { date: format(new Date(user.proUntil), 'd MMMM yyyy', { locale: dateLocale }) })
            : t('account.plan.free')}
        </span>
        {!user.isPro ? (
          <button onClick={() => setPaywallOpen(true)} className="btn-primary text-xs py-1.5 gap-1.5">
            <Icon as={CreditCard} size={14} />
            {t('account.plan.upgrade')}
          </button>
        ) : user.proSource === 'STRIPE' ? (
          <button onClick={openPortal} disabled={opening} className="btn-secondary text-xs py-1.5 gap-1.5">
            {opening && <Spinner size="sm" />}
            {t('account.plan.manage')}
          </button>
        ) : user.proSource === 'GRANT' ? (
          // ADR-0040: a gift has nothing to manage.
          <span className="text-xs text-slate-500 dark:text-slate-400">{t('account.plan.gift')}</span>
        ) : user.proSource ? (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t('account.plan.inStore', { store: STORE_NAME[user.proSource] })}
          </span>
        ) : null}
      </div>
      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
    </Section>
  );
}
